// Tailwind v4 は色を oklch / color-mix(in oklab, ...) で出すが、html2canvas@1 は
// どちらも解釈できず throw する。pro 版はこれらに対応しているのでこちらを使う
import html2canvas from "html2canvas-pro";
import Constants from "common/constant";

// お問い合わせに添える用途なので原寸は不要。長辺をこの幅に収める
const CAPTURE_MAX_WIDTH = 1280;
const CAPTURE_QUALITY = 0.7;

// 報告のためにかぶせた層だけを写さない。
// ユーザーページやチャットなど、報告したい画面そのものも MUI の Dialog なので、
// Dialog を一律で外すと肝心の画面が写らなくなる。外したい層には capture-ignore を付ける
const IGNORED_SELECTOR = ".capture-ignore, .Toastify";

const LOCAL_HOSTNAMES = ["localhost", "127.0.0.1", "::1", "[::1]"];

/**
 * CORS ヘッダを返さない画像を通すための中継 URL。使わないときは undefined。
 *
 * ライブラリ側が new URL() で検証するため絶対 URL が要り、さらに localhost の
 * 中継は拒否される。ローカル開発では Constants.apiHost が "" （vite の同一
 * オリジン中継）なので中継なしで撮る。画像は useCORS で直接読みにいく。
 */
const captureProxyUrl = (): string | undefined => {
  if (!Constants.apiHost) return undefined;
  try {
    const url = new URL(`${Constants.apiHost}/capture/proxy`, window.location.origin);
    return LOCAL_HOSTNAMES.includes(url.hostname) ? undefined : url.href;
  } catch {
    return undefined;
  }
};

// 読み込みが終わっていない画像があると、その部分が欠けたまま撮れてしまう。
// 待ちすぎても仕方がないので上限を設けて先に進む
const ASSET_READY_TIMEOUT_MS = 3000;

const waitForAssets = async () => {
  const images = [...document.images].filter((img) => !img.complete);
  const pending: Promise<unknown>[] = images.map(
    (img) =>
      new Promise((resolve) => {
        img.addEventListener("load", resolve, { once: true });
        img.addEventListener("error", resolve, { once: true });
      }),
  );
  if (document.fonts) pending.push(document.fonts.ready);
  if (pending.length === 0) return;
  await Promise.race([
    Promise.all(pending),
    new Promise((resolve) => setTimeout(resolve, ASSET_READY_TIMEOUT_MS)),
  ]);
};

// 画面が動いている最中に撮ると、切り替えの途中や useScale の拡大率が当たる前の
// 状態がそのまま写る（入った直後に開くと画面がまるごと別物になることがあった）。
// 大きさが2フレーム続けて変わらなくなるまで待つ。動き続ける画面もあるので上限を設ける
const LAYOUT_STABLE_TIMEOUT_MS = 1000;

const layoutSignature = () =>
  [...document.querySelectorAll<HTMLElement>("#root [style*='scale']")]
    .map((element) => {
      const { width, height } = element.getBoundingClientRect();
      return `${Math.round(width)}x${Math.round(height)}`;
    })
    .join(",");

const waitForStableLayout = async () => {
  const deadline = performance.now() + LAYOUT_STABLE_TIMEOUT_MS;
  let previous = "";
  while (performance.now() < deadline) {
    await new Promise((resolve) => requestAnimationFrame(resolve));
    const current = layoutSignature();
    if (current === previous) return;
    previous = current;
  }
};

/**
 * いま表示している画面を JPEG の data URL で返す。
 *
 * Babylon の canvas は preserveDrawingBuffer: true で作られているため一緒に写る
 * （features/courses/hook.ts の useCanvas）。
 * MUI のダイアログとトーストは除くので、開いた状態で呼んでも下の画面が撮れる。
 * 添付は必須ではないので、失敗したときは握りつぶして null を返す。
 */
export const captureScreen = async (): Promise<string | null> => {
  try {
    await waitForAssets();
    await waitForStableLayout();
    const proxy = captureProxyUrl();
    const canvas = await html2canvas(document.body, {
      ...(proxy ? { proxy } : {}),
      useCORS: true,
      scale: Math.min(1, CAPTURE_MAX_WIDTH / window.innerWidth),
      // 既定では body の scrollHeight 分を撮る。body は overflow:hidden でも
      // 中身に引きずられて scrollHeight が伸びる（ユーザー選択から入った直後など）ため、
      // 見えている範囲だけを指定しないと縦長の画像になる
      width: window.innerWidth,
      height: window.innerHeight,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      ignoreElements: (element) => element.matches(IGNORED_SELECTOR),
      logging: false,
    });
    return canvas.toDataURL("image/jpeg", CAPTURE_QUALITY);
  } catch (error) {
    console.error("Failed to capture screen:", error);
    return null;
  }
};
